/**
 * RulesTab — classification rules table (fast-path keyword/regex rules and
 * the classifier-required fallback), each mapped to a refusal template.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Alert, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DataTable, { Column } from '../../common/DataTable';
import StatusBadge from '../../common/StatusBadge';
import RuleEditDialog from './RuleEditDialog';
import { colors } from '../../../theme/designTokens';
import {
  quillApi,
  QuillApiError,
  GuardrailRule,
  RefusalTemplate,
  CreateGuardrailRuleRequest,
  UpdateGuardrailRuleRequest,
} from '../../../services/quillAdminService';
import { verdictColor, verdictLabel, matchTypeLabel } from './guardrailUi';

interface RulesTabProps {
  onTrySandbox?: () => void;
}

const RulesTab: React.FC<RulesTabProps> = ({ onTrySandbox }) => {
  const [rules, setRules] = useState<GuardrailRule[] | null>(null);
  const [templates, setTemplates] = useState<RefusalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingRule, setEditingRule] = useState<GuardrailRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSavedHint, setJustSavedHint] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rulesData, templatesData] = await Promise.all([
        quillApi.guardrails.getRules(),
        quillApi.guardrails.getTemplates(),
      ]);
      setRules(rulesData);
      setTemplates(templatesData);
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to load guardrail rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const templateName = (templateId: string | null) => {
    if (!templateId) return '—';
    const t = templates.find((tpl) => tpl.template_id === templateId);
    return t ? t.template_key : templateId;
  };

  const openCreate = () => {
    setEditingRule(null);
    setDialogMode('create');
    setJustSavedHint(false);
  };

  const openEdit = (rule: GuardrailRule) => {
    setEditingRule(rule);
    setDialogMode('edit');
    setJustSavedHint(false);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogMode(null);
    setEditingRule(null);
  };

  const handleCreate = async (payload: CreateGuardrailRuleRequest) => {
    setSaving(true);
    setSaveError(null);
    try {
      await quillApi.guardrails.createRule(payload);
      setDialogMode(null);
      setJustSavedHint(true);
      await load();
    } catch (e) {
      setSaveError(e instanceof QuillApiError ? e.message : 'Failed to create rule');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (patch: UpdateGuardrailRuleRequest) => {
    if (!editingRule) return;
    setSaving(true);
    setSaveError(null);
    try {
      await quillApi.guardrails.updateRule(editingRule.rule_id, patch);
      setDialogMode(null);
      setEditingRule(null);
      setJustSavedHint(true);
      await load();
    } catch (e) {
      setSaveError(e instanceof QuillApiError ? e.message : 'Failed to update rule');
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<GuardrailRule>[] = [
    {
      id: 'rule_key',
      label: 'Rule Key',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {row.rule_key}
        </Typography>
      ),
    },
    { id: 'rule_name', label: 'Name' },
    {
      id: 'category',
      label: 'Category',
      render: (row) => <Chip label={row.category} size="small" variant="outlined" />,
    },
    {
      id: 'match_type',
      label: 'Match Type',
      render: (row) => <Chip label={matchTypeLabel(row.match_type)} size="small" />,
    },
    {
      id: 'verdict',
      label: 'Verdict',
      render: (row) => {
        const c = verdictColor(row.verdict);
        return (
          <Chip
            label={verdictLabel(row.verdict)}
            size="small"
            sx={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontWeight: 600 }}
          />
        );
      },
    },
    { id: 'priority', label: 'Priority', align: 'right' },
    {
      id: 'refusal_template_id',
      label: 'Template',
      render: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {templateName(row.refusal_template_id)}
        </Typography>
      ),
    },
    {
      id: 'is_active',
      label: 'Status',
      render: (row) => <StatusBadge status={row.is_active ? 'active' : 'inactive'} size="sm" />,
    },
    {
      id: 'actions',
      label: '',
      render: (row) => (
        <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openEdit(row)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Fast-path rules are evaluated by priority (lower first); anything unmatched falls through to the LLM classifier.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ backgroundColor: colors.deepTeal[500], '&:hover': { backgroundColor: colors.deepTeal[600] } }}
        >
          New Rule
        </Button>
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {justSavedHint && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setJustSavedHint(false)}>
          Saved.{' '}
          {onTrySandbox ? (
            <Button size="small" onClick={onTrySandbox} sx={{ ml: 1 }}>
              Try it in the Test Sandbox →
            </Button>
          ) : (
            'Try it in the Test Sandbox tab →'
          )}
        </Alert>
      )}

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <DataTable<GuardrailRule>
          columns={columns}
          data={rules ?? []}
          loading={loading}
          emptyMessage="No guardrail rules yet"
        />
      )}

      {dialogMode && (
        <RuleEditDialog
          mode={dialogMode}
          rule={editingRule}
          templates={templates}
          existingRuleKeys={(rules ?? []).map((r) => r.rule_key)}
          saving={saving}
          onClose={closeDialog}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
        />
      )}
    </Box>
  );
};

export default RulesTab;
